/**
 * קוד HR לתצוגת תשלום / כותרת — ללא UUID, לפי שדות booking_ref על שורת ההזמנה.
 */
import { formatHrBookingRef } from "./bookingRefDisplay";
import { formatHrLabel, isLikelyOpaqueLdsDealToken } from "./resolveCustomerFacingOfferNumber";
import { isTechnicalOrderUuid } from "./resolveOrganizedTourDisplayOrderNumber";

/** @param {unknown} v */
function trimStr(v) {
  if (v == null) return "";
  if (typeof v === "number" && Number.isFinite(v)) return String(v).trim();
  if (typeof v === "string") return v.trim();
  return "";
}

/** @param {unknown} extended */
function readExtendedObject(extended) {
  if (extended == null) return null;
  if (typeof extended === "object") return extended;
  if (typeof extended === "string" && extended.trim()) {
    try {
      const o = JSON.parse(extended);
      return o && typeof o === "object" ? o : null;
    } catch {
      return null;
    }
  }
  return null;
}

/** @param {unknown} raw */
function normalizeHrRefDisplay(raw) {
  const t = trimStr(raw);
  if (!t) return null;
  if (isTechnicalOrderUuid(t)) return null;
  if (isLikelyOpaqueLdsDealToken(t)) return null;

  const compactNoSpace = t.replace(/\s/g, "");
  if (/^HR-/i.test(compactNoSpace)) {
    return compactNoSpace.replace(/^hr-/i, "HR-");
  }

  const fromLabel = formatHrLabel(t);
  if (fromLabel) return fromLabel;

  const digits = t.replace(/\D/g, "");
  if (/^[1-9]\d{7}$/.test(digits)) {
    const hr = formatHrBookingRef(digits);
    if (hr) return hr;
  }

  const hrPlain = formatHrBookingRef(t.replace(/\s/g, ""));
  if (hrPlain) return hrPlain;

  return null;
}

/**
 * @param {Record<string, unknown>|null|undefined} order
 * @param {{ log?: boolean }} [options] — ברירת מחדל: בלי לוג (מניעת רעש בפרופיל); בעמוד תשלום העבירו `{ log: true }`.
 * @returns {{ display: string|null, source: string|null, raw: string|null }}
 */
export function resolveOrganizedTourDisplayOrderRef(order, options) {
  const shouldLog = options?.log === true;
  const out = { display: null, source: null, raw: null };
  if (!order || typeof order !== "object") {
    if (shouldLog && typeof console !== "undefined" && console.info) {
      console.info("[ORG_TOUR_PAYMENT_HR_REF_RESOLVE]", { ...out, reason: "no_order" });
    }
    return out;
  }

  const o = /** @type {Record<string, unknown>} */ (order);
  const or =
    o.order_response && typeof o.order_response === "object"
      ? /** @type {Record<string, unknown>} */ (o.order_response)
      : null;
  const res =
    or?.reservation && typeof or.reservation === "object"
      ? /** @type {Record<string, unknown>} */ (or.reservation)
      : null;
  const ext = readExtendedObject(o.extended);

  /** @type {Array<[string, unknown]>} */
  const tries = [
    ["order.booking_ref", o.booking_ref],
    ["order.bookingRef", o.bookingRef],
    ["order.booking_ref_display", o.booking_ref_display],
    ["order.booking_reference", o.booking_reference],
    ["order.extended.booking_ref", ext?.booking_ref],
    ["order.extended.bookingRef", ext?.bookingRef],
    ["order.order_response.booking_ref", or?.booking_ref],
    ["order.order_response.bookingRef", or?.bookingRef],
    ["order.order_response.reservation.bookingRef", res?.bookingRef],
    ["order.order_response.reservation.booking_ref", res?.booking_ref],
  ];

  for (const [source, val] of tries) {
    const raw = trimStr(val);
    if (!raw) continue;
    const display = normalizeHrRefDisplay(raw);
    if (display) {
      out.display = display;
      out.source = source;
      out.raw = raw;
      break;
    }
  }

  if (shouldLog && typeof console !== "undefined" && console.info) {
    console.info("[ORG_TOUR_PAYMENT_HR_REF_RESOLVE]", {
      display: out.display,
      source: out.source,
      raw: out.raw ? String(out.raw).slice(0, 32) : null,
    });
  }

  return out;
}
